// AI-CONTEXT: See docs/patterns.md#ui-common-table
// Client-side sorting for the shared Table.
//
// Deliberately not a Firestore `.orderBy()`: that silently drops every
// document lacking the field, so a household without a name or registration
// date would vanish from the list instead of sorting to the bottom. Sorting a
// few hundred rows in memory costs nothing and keeps every row visible.
// AI-CONTEXT: See docs/pitfalls.md#database-firestore-orderby-excludes-missing-field

// 'nl' collation with numeric-aware comparison, so 'Kerkstraat 2' comes before
// 'Kerkstraat 10' — house numbers live inside the street label.
const COLLATOR = new Intl.Collator('nl', { sensitivity: 'base', numeric: true })

/**
 * Normalises whatever an accessor returns into something comparable, or null
 * when the value is empty. Empty always sorts to the bottom.
 *
 * @param {*} value
 * @returns {{kind: 'number'|'text', value: number|string}|null}
 */
const toComparable = (value) => {
    if (value === null || value === undefined) return null

    if (value instanceof Date) {
        const time = value.getTime()
        return Number.isNaN(time) ? null : { kind: 'number', value: time }
    }

    // Firestore Timestamp
    if (typeof value.toDate === 'function') {
        const time = value.toDate().getTime()
        return Number.isNaN(time) ? null : { kind: 'number', value: time }
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? { kind: 'number', value } : null
    }

    if (typeof value === 'boolean') return { kind: 'number', value: value ? 1 : 0 }

    const text = String(value).trim()
    return text === '' ? null : { kind: 'text', value: text }
}

/**
 * @param {{kind: string, value: number|string}} a
 * @param {{kind: string, value: number|string}} b
 * @returns {number}
 */
const compare = (a, b) => {
    if (a.kind === 'number' && b.kind === 'number') return a.value - b.value
    return COLLATOR.compare(String(a.value), String(b.value))
}

/**
 * @typedef {object} SortOptions
 * @property {(item: object) => *} [accessor] - Value to sort on, per row
 * @property {'asc'|'desc'|null} [direction] - Omit or pass null to leave the list alone
 * @property {(value: *) => number} [rank] - Sort on an explicit position instead of on the value itself (e.g. a status funnel). A negative result counts as empty.
 */

/**
 * Sorts a copy of `items`. Rows whose value is empty (null, undefined, blank,
 * NaN, invalid date) always end up at the bottom, in both directions. Equal
 * values keep their original order.
 *
 * Without a `direction` or an `accessor` the original array is returned by
 * reference, so callers can rely on identity to skip work.
 *
 * @param {Array<object>} items
 * @param {SortOptions} [options]
 * @returns {Array<object>}
 */
const sortItems = (items, { accessor, direction, rank } = {}) => {
    if (!Array.isArray(items)) return []
    if (!accessor || (direction !== 'asc' && direction !== 'desc')) return items

    const factor = direction === 'desc' ? -1 : 1

    const decorated = items.map((item, index) => {
        const raw = accessor(item)
        const value = rank ? toComparable(rank(raw)) : toComparable(raw)

        return {
            item,
            index,
            // A rank below zero means "not in the list" — bottom, like empty.
            value: value && value.kind === 'number' && rank && value.value < 0 ? null : value,
        }
    })

    decorated.sort((a, b) => {
        if (a.value === null && b.value === null) return a.index - b.index
        if (a.value === null) return 1
        if (b.value === null) return -1

        const result = compare(a.value, b.value)
        return result !== 0 ? result * factor : a.index - b.index
    })

    return decorated.map(entry => entry.item)
}

export default sortItems
